import cn from "@/v2-ui/utils/utils.cn"
import type { BadgeProps } from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import LicenseProductBadge from "@/v2-backoffice-client/license/product/LicenseProductBadge"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import type { LicenseBadgeTooltipLicense } from "@/v2-backoffice-client/license/LicenseBadgeTooltip"

export type LicenseChipProps = {
  /** What the license is called: the catalog license's name, else the product's. */
  name: string
  /** The product the license is for; decides the name half's hue and glyph. */
  productName: string | null | undefined
  /** The catalog license, when it has one: the name half links to its panel (push). */
  planId?: number | null
  /** Status plus whatever lifecycle facts the read carries, for the status half and its hover. */
  license: LicenseBadgeTooltipLicense
  /** Status display name from the getLicenseStatuses lookup. */
  statusName?: string
  size?: BadgeProps["size"]
  className?: string
}

/**
 * A customer's license as ONE chip: the product-toned name on the left
 * (linking to the catalog license), the status fused to its right in the
 * status's own hue and icon, with its countdown or date stamp. Two pills
 * per license read as two licenses; this reads as one, and the product
 * hue tells Placepoint from an MCP server before the name is read.
 */
function LicenseChip(props: LicenseChipProps) {
  const {
    name,
    productName,
    planId,
    license,
    statusName,
    size = "sm",
    className,
  } = props

  const nameBadge = (
    <LicenseProductBadge
      className="license-chip-name"
      size={size}
      productName={productName}
      title={productName ? `${name} · ${productName}` : name}
    >
      {name}
    </LicenseProductBadge>
  )

  return (
    <span className={cn("license-chip", className)}>
      {planId != null ? (
        // No link glyph: it would sit between the two halves and split the
        // chip back into two pills (the hover tint is the affordance).
        <Link
          variant="unstyled"
          iconSrc={null}
          className="license-chip-link"
          to={`>>?v=plan:${planId}`}
          title={`Open ${name}`}
        >
          {nameBadge}
        </Link>
      ) : (
        nameBadge
      )}
      <LicenseStatusBadge
        className="license-chip-status"
        size={size}
        license={license}
        statusName={statusName}
      />
    </span>
  )
}

export default LicenseChip
